// src/pages/CheckoutScreen.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useApp } from '../lib/AppContext';
import { t } from '../lib/i18n';
import { formatEurCheckout, calcLoyaltyPoints, COUNTRY_CONFIG } from '../lib/currency';
import { validateDiscountCode, fetchBestUserReward, markRewardRedeemed } from '../lib/discounts';
import { validateCartForCountry, logComplianceEvent } from '../lib/compliance';

// ── Payment method icons (SVG inline — no external deps) ─────────────────────
const PaymentIcons = () => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, flexWrap: 'wrap', marginTop: 12 }}>
    {[
      { label: 'VISA',   bg: '#1a1f71', color: '#fff',     text: 'VISA' },
      { label: 'MC',     bg: '#eb001b', color: '#fff',     text: '●●' },
      { label: 'PayPal', bg: '#003087', color: '#fff',     text: 'PayPal' },
      { label: 'iDEAL',  bg: '#cc0066', color: '#fff',     text: 'iDEAL' },
      { label: 'SEPA',   bg: '#2c3e50', color: '#aaa',     text: 'SEPA' },
      { label: 'BTC',    bg: '#f7931a', color: '#000',     text: '₿' },
    ].map(p => (
      <div key={p.label} style={{
        background: p.bg, color: p.color,
        borderRadius: 5, padding: '3px 7px',
        fontSize: '.62rem', fontWeight: 800,
        fontFamily: "'Barlow Condensed', sans-serif",
        letterSpacing: '.04em', whiteSpace: 'nowrap',
      }}>{p.text}</div>
    ))}
    <div style={{ fontSize: '.62rem', color: '#444', fontFamily: "'Barlow Condensed', sans-serif" }}>
      + more via Mollie
    </div>
  </div>
);

// ── Free shipping progress bar ───────────────────────────────────────────────
const ShippingProgress = ({ subtotal, threshold = 60 }) => {
  const pct = Math.min((subtotal / threshold) * 100, 100);
  const remaining = Math.max(0, threshold - subtotal);
  const done = subtotal >= threshold;

  return (
    <div style={{
      background: done ? 'rgba(76,202,116,.08)' : 'rgba(255,255,255,.04)',
      border: `1px solid ${done ? 'rgba(76,202,116,.25)' : 'rgba(255,255,255,.08)'}`,
      borderRadius: 10, padding: '10px 14px', marginBottom: 14,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <span style={{ fontSize: '.75rem', color: done ? '#4cca74' : '#888' }}>
          {done ? '✓ Free shipping unlocked!' : `€${remaining.toFixed(2)} away from free shipping`}
        </span>
        <span style={{ fontSize: '.72rem', color: '#555' }}>€{threshold}</span>
      </div>
      <div style={{ height: 4, background: '#1a1a1a', borderRadius: 2, overflow: 'hidden' }}>
        <div style={{
          height: '100%',
          background: done ? '#4cca74' : 'linear-gradient(90deg, #4cca74, #3bb85e)',
          borderRadius: 2,
          width: `${pct}%`,
          transition: 'width .4s ease',
        }} />
      </div>
    </div>
  );
};

// ── Trust badges ─────────────────────────────────────────────────────────────
const TrustBar = () => (
  <div style={{
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))',
    gap: 8, marginTop: 14,
  }}>
    {[
      { icon: '🔒', text: 'SSL Secured' },
      { icon: '📦', text: 'Discreet Packaging' },
      { icon: '↩️', text: '14-Day Returns' },
      { icon: '⚡', text: '1-2 Day Dispatch' },
    ].map(b => (
      <div key={b.text} style={{
        display: 'flex', alignItems: 'center', gap: 6,
        background: 'rgba(255,255,255,.03)',
        border: '1px solid rgba(255,255,255,.06)',
        borderRadius: 8, padding: '7px 10px',
      }}>
        <span style={{ fontSize: '.85rem', flexShrink: 0 }}>{b.icon}</span>
        <span style={{ fontSize: '.68rem', color: '#666', lineHeight: 1.3, fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: '.03em' }}>
          {b.text}
        </span>
      </div>
    ))}
  </div>
);

// ── Field wrapper with inline validation ─────────────────────────────────────
const Field = ({ label, required, error, children }) => (
  <div style={{ marginBottom: 16 }}>
    <div style={{
      fontFamily: "'Barlow Condensed', sans-serif",
      fontSize: '.7rem', fontWeight: 700, letterSpacing: '.1em',
      textTransform: 'uppercase', color: error ? '#e05050' : '#555',
      marginBottom: 5,
    }}>
      {label}{required ? ' *' : ''}
    </div>
    {children}
    {error && (
      <div style={{ fontSize: '.72rem', color: '#e05050', marginTop: 4 }}>
        {error}
      </div>
    )}
  </div>
);

// ── Main component ────────────────────────────────────────────────────────────
export default function CheckoutScreen() {
  const {
    lang, country, cartItems, cartSubtotalEur, cartShippingEur,
    user, profile, clearCart, showToast, displayCurrency, fmt,
  } = useApp();
  const navigate = useNavigate();
  const summaryRef = useRef(null);

  const [form, setForm] = useState({
    full_name:    profile?.full_name || '',
    email:        user?.email || '',
    address_line1: '',
    city:         '',
    postal_code:  '',
    country_code: country,
    phone:        '',
  });
  const [touched, setTouched]   = useState({});
  const [loading, setLoading]   = useState(false);
  const update  = (f, v) => setForm(x => ({ ...x, [f]: v }));
  const touch   = (f)    => setTouched(t => ({ ...t, [f]: true }));

  // Discount state
  const [discountInput,   setDiscountInput]   = useState('');
  const [discountResult,  setDiscountResult]  = useState(null);
  const [discountLoading, setDiscountLoading] = useState(false);
  const [autoReward,      setAutoReward]      = useState(null);
  const [autoLoading,     setAutoLoading]     = useState(true);
  const [codeOpen,        setCodeOpen]        = useState(false);

  // Compliance state
  const [complianceCheck,  setComplianceCheck]  = useState(null);  // null = unchecked
  const [complianceLoading, setComplianceLoading] = useState(false);

  // Re-run compliance when country or cart changes
  useEffect(() => {
    if (!cartItems.length || !form.country_code) return;
    setComplianceLoading(true);
    validateCartForCountry(cartItems, form.country_code).then(result => {
      setComplianceCheck(result);
      setComplianceLoading(false);
      if (!result.valid && result.blockedItems.length) {
        logComplianceEvent('checkout_blocked', {
          userId:  user?.id,
          country: form.country_code,
          details: { blockedItems: result.blockedItems.map(i => ({ id: i.id, name: i.name?.en })) },
        });
      }
    });
  }, [form.country_code, cartItems.length]); // eslint-disable-line

  // Derived totals
  const effectiveShipping = discountResult?.isFreeShipping ? 0 : cartShippingEur;
  const discountEur       = discountResult?.discountEur || 0;
  const finalTotal        = Math.max(0, cartSubtotalEur - discountEur + effectiveShipping);
  const savedAmount       = discountEur + (discountResult?.isFreeShipping ? cartShippingEur : 0);

  // Auto-load best user reward
  useEffect(() => {
    if (!user) { setAutoLoading(false); return; }
    fetchBestUserReward(user.id, cartSubtotalEur).then(reward => {
      setAutoReward(reward);
      setAutoLoading(false);
      // Auto-open the discount section if they have a reward waiting
      if (reward) setCodeOpen(true);
    });
  }, [user]); // eslint-disable-line

  // Inline validation
  const errors = {
    full_name:     touched.full_name    && !form.full_name.trim()     ? 'Full name is required' : '',
    email:         touched.email        && !/\S+@\S+\.\S+/.test(form.email) ? 'Valid email required' : '',
    address_line1: touched.address_line1 && !form.address_line1.trim() ? 'Address is required' : '',
    city:          touched.city         && !form.city.trim()           ? 'City is required' : '',
    postal_code:   touched.postal_code  && !form.postal_code.trim()    ? 'Postal code is required' : '',
  };

  const applyAutoReward = () => {
    if (!autoReward) return;
    setDiscountResult(autoReward);
    setDiscountInput(autoReward.code);
    showToast(`✓ ${autoReward.displayLabel} applied!`, 'success');
  };

  const applyManualCode = async () => {
    if (!discountInput.trim()) return;
    setDiscountLoading(true);
    const result = await validateDiscountCode(discountInput, cartSubtotalEur, user?.id);
    setDiscountLoading(false);
    if (result.valid) {
      setDiscountResult(result);
      showToast(result.message, 'success');
    } else {
      showToast(result.message, 'error');
    }
  };

  const removeDiscount = () => { setDiscountResult(null); setDiscountInput(''); };

  const handlePay = async () => {
    // Touch all required fields to show validation
    const required = ['full_name', 'email', 'address_line1', 'city', 'postal_code'];
    setTouched(Object.fromEntries(required.map(k => [k, true])));

    if (cartItems.length === 0) { showToast('Your cart is empty', 'error'); return; }
    if (!form.full_name || !form.email || !form.address_line1 || !form.city || !form.postal_code) {
      showToast('Please fill in all required fields', 'error');
      return;
    }
    if (!/\S+@\S+\.\S+/.test(form.email)) {
      showToast('Please enter a valid email address', 'error');
      return;
    }

    // ── Compliance hard block ─────────────────────────────────────────────
    // Re-validate at payment time (not just on render) to prevent race conditions
    const compResult = await validateCartForCountry(cartItems, form.country_code);
    if (!compResult.valid) {
      if (compResult.countryBlocked) {
        showToast(`Orders to ${form.country_code} are not available in your region.`, 'error');
      } else {
        const names = compResult.blockedItems.map(i => i.name?.en || 'item').join(', ');
        showToast(`These items cannot be shipped to ${form.country_code}: ${names}`, 'error');
      }
      await logComplianceEvent('checkout_blocked_payment', {
        userId:  user?.id,
        country: form.country_code,
        details: {
          countryBlocked: compResult.countryBlocked,
          blockedItems:   compResult.blockedItems.map(i => ({ id: i.id, name: i.name?.en })),
        },
      });
      return;
    }

    setLoading(true);
    try {
      const orderItems = cartItems.map(item => ({
        product_id: item.id,
        name:       item.name?.en || item.name,
        qty:        item.qty,
        price_eur:  item.price_eur,
      }));

      const { data: order, error: orderError } = await supabase.from('orders').insert({
        user_id:               user.id,
        status:                'pending',
        items:                 orderItems,
        subtotal_eur:          parseFloat(cartSubtotalEur.toFixed(2)),
        shipping_eur:          parseFloat(effectiveShipping.toFixed(2)),
        total_eur:             parseFloat(finalTotal.toFixed(2)),
        display_currency:      displayCurrency,
        customer_email:        form.email,
        customer_name:         form.full_name,
        loyalty_points_earned: calcLoyaltyPoints(finalTotal),
        collector_consent:     true,
        age_verified:          true,
        discount_code:         discountResult?.code || null,
        discount_eur:          parseFloat(discountEur.toFixed(2)),
        shipping_address: {
          full_name:     form.full_name,
          address_line1: form.address_line1,
          city:          form.city,
          postal_code:   form.postal_code,
          country_code:  form.country_code,
          phone:         form.phone,
        },
      }).select().single();

      if (orderError) throw orderError;

      if (discountResult?.code) {
        await markRewardRedeemed(discountResult.code, user.id);
        if (discountResult.dbRecord?.source === 'admin') {
          await supabase.from('discount_codes')
            .update({ current_uses: (discountResult.dbRecord.current_uses || 0) + 1 })
            .eq('code', discountResult.code);
        }
      }

      const payRes = await fetch('/.netlify/functions/create-payment', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({
          orderId:       order.id,
          totalEur:      parseFloat(finalTotal.toFixed(2)),
          customerEmail: form.email,
          customerName:  form.full_name,
          returnUrl:     `${window.location.origin}/order-success?id=${order.id}`,
        }),
      });
      const payData = await payRes.json();
      if (!payRes.ok || !payData.checkoutUrl) throw new Error(payData.error || 'Payment creation failed');
      clearCart();
      window.location.href = payData.checkoutUrl;
    } catch (err) {
      showToast(err.message || 'Something went wrong. Please try again.', 'error');
      setLoading(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="page-container" style={{ maxWidth: 980, padding: '32px 20px 80px' }}>

      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 28 }}>
        <img src="/logo.png" alt="CosmiqHerbz" className="checkout-logo" style={{ marginBottom: 0 }} />
        <div>
          <h1 style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: '1.9rem', letterSpacing: '.05em', marginBottom: 2 }}>
            {t('checkout_title', lang)}
          </h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: '.75rem', color: '#555' }}>
            <span>🔒 SSL Secured</span>
            <span>·</span>
            <span>Powered by Mollie</span>
          </div>
        </div>
      </div>

      {/* ── Free shipping progress (above the fold) ── */}
      <ShippingProgress subtotal={cartSubtotalEur} threshold={60} />

      <div className="checkout-grid">

        {/* ══ LEFT: Delivery form ══ */}
        <div>

          {/* Reward banner — shown prominently if a reward exists */}
          {!autoLoading && autoReward && !discountResult && (
            <div style={{
              background: 'rgba(76,202,116,.1)',
              border: '1px solid rgba(76,202,116,.35)',
              borderRadius: 12, padding: '14px 16px', marginBottom: 20,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
            }}>
              <div>
                <div style={{ fontSize: '.72rem', color: '#4cca74', fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 2 }}>
                  🎰 You have a reward waiting!
                </div>
                <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: '1.05rem', fontWeight: 700, color: '#f4f4f4' }}>
                  {autoReward.displayLabel}
                </div>
                <div style={{ fontFamily: 'monospace', fontSize: '.75rem', color: '#555', marginTop: 1 }}>
                  {autoReward.code}
                </div>
              </div>
              <button
                onClick={applyAutoReward}
                style={{
                  background: '#4cca74', border: 'none', borderRadius: 20,
                  padding: '10px 18px', color: '#000',
                  fontFamily: "'Barlow Condensed', sans-serif", fontSize: '.82rem',
                  fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase',
                  cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
                }}
              >
                Apply Now
              </button>
            </div>
          )}

          {/* Applied discount confirmation */}
          {discountResult && (
            <div style={{
              background: 'rgba(76,202,116,.08)',
              border: '1px solid rgba(76,202,116,.3)',
              borderRadius: 12, padding: '12px 16px', marginBottom: 20,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: '1.2rem' }}>🎉</span>
                <div>
                  <div style={{ fontSize: '.72rem', color: '#4cca74', fontWeight: 700, fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: '.08em', textTransform: 'uppercase' }}>
                    Discount Applied
                  </div>
                  <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: '.9rem', fontWeight: 700, color: '#f4f4f4' }}>
                    {discountResult.displayLabel}
                    {savedAmount > 0 && <span style={{ color: '#4cca74', marginLeft: 8 }}>— Save {formatEurCheckout(savedAmount)}</span>}
                  </div>
                </div>
              </div>
              <button onClick={removeDiscount} style={{ background: 'none', border: 'none', color: '#e05050', cursor: 'pointer', fontSize: '.75rem', fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700, padding: '4px 8px', flexShrink: 0 }}>
                Remove ✕
              </button>
            </div>
          )}

          {/* Section heading */}
          <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: '.72rem', fontWeight: 700, letterSpacing: '.2em', textTransform: 'uppercase', color: '#4cca74', marginBottom: 16 }}>
            Delivery Details
          </div>

          {/* Name + Email side by side on desktop */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0 14px' }}>
            <Field label="Full Name" required error={errors.full_name}>
              <input
                className={`input-field${errors.full_name ? ' input-error' : ''}`}
                value={form.full_name}
                onChange={e => update('full_name', e.target.value)}
                onBlur={() => touch('full_name')}
                autoComplete="name"
                placeholder="Jane Doe"
              />
            </Field>
            <Field label="Email Address" required error={errors.email}>
              <input
                className={`input-field${errors.email ? ' input-error' : ''}`}
                type="email"
                value={form.email}
                onChange={e => update('email', e.target.value)}
                onBlur={() => touch('email')}
                autoComplete="email"
                placeholder="you@example.com"
              />
            </Field>
          </div>

          <Field label="Street Address" required error={errors.address_line1}>
            <input
              className={`input-field${errors.address_line1 ? ' input-error' : ''}`}
              value={form.address_line1}
              onChange={e => update('address_line1', e.target.value)}
              onBlur={() => touch('address_line1')}
              autoComplete="street-address"
              placeholder="123 Main Street, Apt 4"
            />
          </Field>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <Field label="City" required error={errors.city}>
              <input
                className={`input-field${errors.city ? ' input-error' : ''}`}
                value={form.city}
                onChange={e => update('city', e.target.value)}
                onBlur={() => touch('city')}
                autoComplete="address-level2"
                placeholder="Berlin"
              />
            </Field>
            <Field label="Postal Code" required error={errors.postal_code}>
              <input
                className={`input-field${errors.postal_code ? ' input-error' : ''}`}
                value={form.postal_code}
                onChange={e => update('postal_code', e.target.value)}
                onBlur={() => touch('postal_code')}
                autoComplete="postal-code"
                placeholder="10115"
              />
            </Field>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0 14px' }}>
            <Field label="Country" required>
              <select
                className="input-field"
                value={form.country_code}
                onChange={e => update('country_code', e.target.value)}
                autoComplete="country"
                style={{ appearance: 'none' }}
              >
                {Object.entries(COUNTRY_CONFIG).map(([code, cfg]) => (
                  <option key={code} value={code}>{cfg.flag} {cfg.name}</option>
                ))}
              </select>
            </Field>

            {/* ── Compliance alerts ── */}
            {complianceLoading && (
              <div style={{ fontSize: '.78rem', color: '#555', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
                <div className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} />
                Checking availability for {form.country_code}…
              </div>
            )}
            {!complianceLoading && complianceCheck && complianceCheck.countryBlocked && (
              <div style={{ background: 'rgba(224,80,80,.08)', border: '1px solid rgba(224,80,80,.3)', borderRadius: 10, padding: '12px 14px', marginBottom: 14 }}>
                <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: '.78rem', fontWeight: 700, color: '#e05050', marginBottom: 4 }}>
                  🚫 Orders to {form.country_code} are not currently available
                </div>
                <div style={{ fontSize: '.75rem', color: '#888', lineHeight: 1.6 }}>
                  {complianceCheck.countryBlockReason || 'This region is not supported at this time.'}
                </div>
              </div>
            )}
            {!complianceLoading && complianceCheck && !complianceCheck.countryBlocked && complianceCheck.blockedItems.length > 0 && (
              <div style={{ background: 'rgba(224,80,80,.08)', border: '1px solid rgba(224,80,80,.3)', borderRadius: 10, padding: '12px 14px', marginBottom: 14 }}>
                <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: '.78rem', fontWeight: 700, color: '#e05050', marginBottom: 6 }}>
                  ⚠️ Some items cannot be shipped to {form.country_code}
                </div>
                {complianceCheck.blockedItems.map(item => (
                  <div key={item.id} style={{ fontSize: '.75rem', color: '#888', marginBottom: 3 }}>
                    • {item.name?.en || 'Item'}{item.rule?.reason ? ` — ${item.rule.reason}` : ''}
                  </div>
                ))}
                <div style={{ fontSize: '.72rem', color: '#555', marginTop: 6 }}>
                  Please remove these items or choose a different shipping country to continue.
                </div>
              </div>
            )}
            {!complianceLoading && complianceCheck && !complianceCheck.countryBlocked && complianceCheck.restrictedItems.length > 0 && (
              <div style={{ background: 'rgba(240,165,0,.06)', border: '1px solid rgba(240,165,0,.2)', borderRadius: 10, padding: '12px 14px', marginBottom: 14 }}>
                <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: '.78rem', fontWeight: 700, color: '#f0a500', marginBottom: 4 }}>
                  ℹ️ Restricted items for {form.country_code}
                </div>
                {complianceCheck.restrictedItems.map(item => (
                  <div key={item.id} style={{ fontSize: '.75rem', color: '#888', marginBottom: 2 }}>
                    • {item.name?.en || 'Item'}: {item.rule?.reason || 'Subject to local regulations'}
                  </div>
                ))}
              </div>
            )}
            <Field label="Phone (optional)">
              <input
                className="input-field"
                type="tel"
                value={form.phone}
                onChange={e => update('phone', e.target.value)}
                autoComplete="tel"
                placeholder="+49 123 456 7890"
              />
            </Field>
          </div>

          {/* ── Discount code accordion ── */}
          {!discountResult && (
            <div style={{ marginTop: 8 }}>
              <button
                onClick={() => setCodeOpen(o => !o)}
                style={{
                  background: 'none', border: 'none', padding: '8px 0',
                  color: '#555', cursor: 'pointer', display: 'flex',
                  alignItems: 'center', gap: 6, fontSize: '.8rem',
                  fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700,
                  letterSpacing: '.06em',
                }}
              >
                <span style={{ transition: 'transform .2s', display: 'inline-block', transform: codeOpen ? 'rotate(90deg)' : 'none' }}>›</span>
                Have a discount code?
              </button>
              {codeOpen && (
                <div style={{ paddingTop: 8 }}>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input
                      className="input-field"
                      value={discountInput}
                      onChange={e => setDiscountInput(e.target.value.toUpperCase())}
                      onKeyDown={e => e.key === 'Enter' && applyManualCode()}
                      placeholder="e.g. WELCOME10"
                      style={{ flex: 1, fontFamily: 'monospace', letterSpacing: '.06em', textTransform: 'uppercase' }}
                      autoFocus
                    />
                    <button
                      onClick={applyManualCode}
                      disabled={discountLoading || !discountInput.trim()}
                      style={{
                        background: discountLoading || !discountInput.trim() ? 'rgba(255,255,255,.05)' : 'rgba(76,202,116,.12)',
                        border: `1px solid ${discountLoading || !discountInput.trim() ? 'rgba(255,255,255,.08)' : 'rgba(76,202,116,.35)'}`,
                        borderRadius: 10, padding: '0 18px',
                        color: discountLoading || !discountInput.trim() ? '#444' : '#4cca74',
                        fontFamily: "'Barlow Condensed', sans-serif", fontSize: '.85rem',
                        fontWeight: 700, cursor: discountInput.trim() ? 'pointer' : 'default',
                        whiteSpace: 'nowrap', minHeight: 44, transition: 'all .2s',
                      }}
                    >
                      {discountLoading ? '…' : 'Apply'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Trust badges — bottom of form */}
          <TrustBar />
        </div>

        {/* ══ RIGHT: Order summary ══ */}
        <div className="checkout-summary" ref={summaryRef}>

          <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: '.72rem', fontWeight: 700, letterSpacing: '.18em', textTransform: 'uppercase', color: '#4cca74', marginBottom: 14 }}>
            {t('order_summary', lang)}
          </div>

          {/* Items with thumbnails */}
          <div style={{ marginBottom: 14 }}>
            {cartItems.map(item => {
              const name = item.name?.[lang] || item.name?.en || 'Product';
              return (
                <div key={item.id} style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 10 }}>
                  {/* Thumbnail */}
                  <div style={{
                    width: 42, height: 42, borderRadius: 8, flexShrink: 0,
                    background: '#1a1a1a', overflow: 'hidden',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    position: 'relative',
                  }}>
                    {item.images?.[0]
                      ? <img src={item.images[0]} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      : <span style={{ fontSize: '1rem', opacity: .4 }}>🌿</span>
                    }
                    {/* Qty badge */}
                    <div style={{
                      position: 'absolute', top: -4, right: -4,
                      background: '#4cca74', color: '#000',
                      borderRadius: '50%', width: 16, height: 16,
                      fontSize: '.55rem', fontWeight: 800,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      {item.qty}
                    </div>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '.82rem', color: '#ddd', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {name}
                    </div>
                    <div style={{ fontSize: '.72rem', color: '#555' }}>
                      {fmt(item.price_eur)} ea.
                    </div>
                  </div>
                  <div style={{ fontSize: '.85rem', fontWeight: 700, color: '#f4f4f4', flexShrink: 0 }}>
                    {formatEurCheckout(item.price_eur * item.qty)}
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{ height: 1, background: 'rgba(255,255,255,.07)', margin: '4px 0 12px' }} />

          {/* Pricing rows */}
          <div style={{ fontSize: '.85rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
              <span style={{ color: '#777' }}>{t('subtotal', lang)}</span>
              <span>{formatEurCheckout(cartSubtotalEur)}</span>
            </div>

            {discountEur > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                <span style={{ color: '#4cca74' }}>🎉 {discountResult.displayLabel}</span>
                <span style={{ color: '#4cca74', fontWeight: 700 }}>−{formatEurCheckout(discountEur)}</span>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ color: '#777' }}>
                {t('shipping', lang)}
                {effectiveShipping === 0 && (
                  <span style={{ fontSize: '.68rem', color: '#4cca74', marginLeft: 4 }}>FREE</span>
                )}
              </span>
              <span style={{ color: effectiveShipping === 0 ? '#4cca74' : '#f4f4f4' }}>
                {discountResult?.isFreeShipping
                  ? '🎁 Free (reward)'
                  : effectiveShipping === 0
                    ? t('free_shipping', lang)
                    : formatEurCheckout(effectiveShipping)}
              </span>
            </div>

            {/* Estimated delivery */}
            <div style={{ fontSize: '.72rem', color: '#444', textAlign: 'right', marginBottom: 12 }}>
              Est. delivery 3–6 business days
            </div>
          </div>

          {/* Total */}
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            paddingTop: 14, borderTop: '1px solid rgba(255,255,255,.07)', marginBottom: 6,
          }}>
            <span style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', fontSize: '.88rem' }}>
              {t('total', lang)}
            </span>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '1.9rem', color: '#4cca74', letterSpacing: '.04em', lineHeight: 1 }}>
                {formatEurCheckout(finalTotal)}
              </div>
              {savedAmount > 0 && (
                <div style={{ fontSize: '.7rem', color: '#4cca74', marginTop: 2 }}>
                  You save {formatEurCheckout(savedAmount)}!
                </div>
              )}
            </div>
          </div>

          {/* Loyalty points preview */}
          {calcLoyaltyPoints(finalTotal) > 0 && (
            <div style={{ fontSize: '.72rem', color: '#555', marginBottom: 14, padding: '6px 10px', background: 'rgba(76,202,116,.04)', borderRadius: 6, border: '1px solid rgba(76,202,116,.1)' }}>
              ⭐ You'll earn <strong style={{ color: '#4cca74' }}>{calcLoyaltyPoints(finalTotal)} loyalty points</strong> on this order
            </div>
          )}

          {/* CTA */}
          <button
            className="btn-primary"
            style={{ width: '100%', fontSize: '1rem', padding: '15px 24px', letterSpacing: '.06em', opacity: (complianceCheck && !complianceCheck.valid) ? .5 : 1 }}
            onClick={handlePay}
            disabled={loading || (complianceCheck && !complianceCheck.valid)}
          >
            {loading
              ? <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}>
                  <div className="spinner" style={{ width: 18, height: 18, borderWidth: 2 }} />
                  Processing…
                </div>
              : <>
                  🔒 {t('pay_now', lang)} — {formatEurCheckout(finalTotal)}
                </>
            }
          </button>

          {/* Payment icons */}
          <PaymentIcons />

          {/* Legal note */}
          <p style={{ textAlign: 'center', fontSize: '.68rem', color: '#333', marginTop: 10, lineHeight: 1.7 }}>
            By placing this order you confirm you are 18+ and accept our{' '}
            <a href="/legal/terms" style={{ color: '#555' }}>Terms &amp; Conditions</a>.
            All products are sold as collector's items only.
          </p>
        </div>
      </div>

      {/* ── Sticky mobile pay bar ── */}
      <div style={{
        position: 'fixed', bottom: 0, left: 0, right: 0,
        background: 'rgba(10,10,10,.97)',
        borderTop: '1px solid rgba(255,255,255,.08)',
        padding: '12px 16px',
        zIndex: 150,
        display: 'none', // shown via CSS class below
      }} className="mobile-pay-bar">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, maxWidth: 600, margin: '0 auto' }}>
          <div>
            <div style={{ fontSize: '.7rem', color: '#555', fontFamily: "'Barlow Condensed', sans-serif" }}>Total</div>
            <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '1.4rem', color: '#4cca74', letterSpacing: '.04em', lineHeight: 1 }}>
              {formatEurCheckout(finalTotal)}
            </div>
            {savedAmount > 0 && (
              <div style={{ fontSize: '.65rem', color: '#4cca74' }}>Saving {formatEurCheckout(savedAmount)}</div>
            )}
          </div>
          <button
            className="btn-primary"
            style={{ flex: 1, maxWidth: 260, padding: '13px 20px' }}
            onClick={handlePay}
            disabled={loading}
          >
            {loading ? 'Processing…' : `🔒 Pay — ${formatEurCheckout(finalTotal)}`}
          </button>
        </div>
      </div>

      <style>{`
        .input-error { border-color: #e05050 !important; }
        @media (max-width: 768px) {
          .mobile-pay-bar { display: block !important; }
        }
      `}</style>
    </div>
  );
}